import { z } from "zod";
import { BUDGET_CATEGORY, COLLABORATOR_ROLE, SUPPORTED_CURRENCIES, type Plan } from "../enums";
import type { ItemFeature, PremiumFeature } from "../free-tier";
import type { PlanFeature } from "../plan-limits";
import { isoDate, isoDateNullable, money, trimmed, uuid } from "./common";

export const categoryBudgetsSchema = z.partialRecord(z.enum(BUDGET_CATEGORY), money);
export type CategoryBudgets = z.infer<typeof categoryBudgetsSchema>;

export const createWeddingSchema = z.object({
  partnerOneName: trimmed(80).default(""),
  partnerTwoName: trimmed(80).default(""),
  weddingDate: isoDate,
  venueName: trimmed(120).default(""),
  venueAddress: trimmed(240).default(""),
  theme: trimmed(80).default(""),
  currencyCode: z.enum(SUPPORTED_CURRENCIES).default("USD"),
  budgetTotal: money.default(0),
  /** Seed the 34-task checklist + 16 budget lines (default on, like iOS). */
  seedDefaults: z.boolean().default(true),
});
export type CreateWedding = z.infer<typeof createWeddingSchema>;
/** What a client sends: defaults optional, dates as ISO strings (`CreateWedding` is what the API reads). */
export type CreateWeddingBody = z.input<typeof createWeddingSchema>;

export const updateWeddingSchema = z.object({
  partnerOneName: trimmed(80).optional(),
  partnerTwoName: trimmed(80).optional(),
  weddingDate: isoDate.optional(),
  venueName: trimmed(120).optional(),
  venueAddress: trimmed(240).optional(),
  theme: trimmed(80).optional(),
  currencyCode: z.enum(SUPPORTED_CURRENCIES).optional(),
  budgetTotal: money.optional(),
  rsvpDeadline: isoDateNullable.optional(),
  categoryBudgets: categoryBudgetsSchema.optional(),
});
export type UpdateWedding = z.infer<typeof updateWeddingSchema>;
export type UpdateWeddingBody = z.input<typeof updateWeddingSchema>;

/** What the API returns. Money fields are `null` when masked for the viewer. */
export interface WeddingDTO {
  id: string;
  partnerOneName: string;
  partnerTwoName: string;
  weddingDate: string;
  venueName: string;
  venueAddress: string;
  theme: string;
  currencyCode: string;
  budgetTotal: number | null;
  rsvpDeadline: string | null;
  categoryBudgets: CategoryBudgets | null;
  /** Set when the owner archived the wedding: read-only for everyone, exportable, does not count as active. */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WeddingSummaryDTO {
  id: string;
  partnerOneName: string;
  partnerTwoName: string;
  weddingDate: string;
  role: (typeof COLLABORATOR_ROLE)[number];
  isOwner: boolean;
  archivedAt: string | null;
}

const memberGrantsSchema = z.record(z.string(), z.enum(["hidden", "view", "edit"]));

export const updateMemberSchema = z.object({
  role: z.enum(COLLABORATOR_ROLE.filter((r) => r !== "owner") as unknown as [string, ...string[]]).optional(),
  grants: memberGrantsSchema.optional(),
  seeFinancials: z.boolean().optional(),
  displayName: trimmed(80).optional(),
});
export type UpdateMember = z.infer<typeof updateMemberSchema>;
export type UpdateMemberBody = z.input<typeof updateMemberSchema>;

export interface MemberDTO {
  id: string;
  userId: string;
  displayName: string;
  email: string | null;
  role: (typeof COLLABORATOR_ROLE)[number];
  grants: Record<string, "hidden" | "view" | "edit">;
  seeFinancials: boolean;
  isOwner: boolean;
  revokedAt: string | null;
  joinedAt: string;
}

export const createInviteSchema = z.object({
  role: z.enum(["coOwner", "planner", "editor", "viewer", "reader", "guestHelper", "custom"]).default("reader"),
  expiresInDays: z.number().int().min(1).max(90).default(14),
  maxUses: z.number().int().min(1).max(50).default(10),
  seatBound: z.boolean().default(false),
  displayName: trimmed(80).default(""),
  /** Seat overrides, like membership updates: every non-owner role may hold them, including financial viewers. */
  grants: memberGrantsSchema.optional(),
  seeFinancials: z.boolean().optional(),
}).transform((input) => ({ ...input, maxUses: input.seatBound ? 1 : input.maxUses }));
export type CreateInvite = z.infer<typeof createInviteSchema>;
export type CreateInviteBody = z.input<typeof createInviteSchema>;

export const acceptInviteSchema = z.object({
  /** A seat may supply the wedding name; ordinary links still require one in the service. */
  displayName: trimmed(80).min(1).optional().default(""),
});

export interface InviteDTO {
  token: string;
  role: string;
  expiresAt: string;
  maxUses: number;
  usedCount: number;
  revokedAt: string | null;
  createdAt: string;
  url: string;
  seatBound: boolean;
  displayName: string;
  /** Owner-facing only; never included in the public preview. */
  grants: Record<string, "hidden" | "view" | "edit">;
  seeFinancials: boolean;
}

export interface InvitePreviewDTO {
  coupleTitle: string;
  weddingDate: string;
  role: string;
  valid: boolean;
  seatBound: boolean;
  displayName: string;
}

export { uuid };

/* ───────────────────────── Ownership hand-off (Pro) ───────────────────────── */

/** `POST /weddings/:id/transfer` — the owner (Pro+) makes an existing member the owner and stays on as planner. */
export const transferWeddingSchema = z.object({ toMemberId: uuid });
export type TransferWedding = z.infer<typeof transferWeddingSchema>;

/* ───────────────────────── Templates (Pro) ───────────────────────── */

export const TEMPLATE_KIND = ["checklist", "budget", "timeline"] as const;
export type TemplateKind = (typeof TEMPLATE_KIND)[number];

/** `POST /weddings/:id/templates` — snapshot one section of this wedding as a reusable template. */
export const saveTemplateSchema = z.object({
  kind: z.enum(TEMPLATE_KIND),
  name: trimmed(80).min(1),
});
export type SaveTemplate = z.infer<typeof saveTemplateSchema>;

export const checklistTemplateItem = z.object({ title: trimmed(200), details: trimmed(2000).default(""), timeframe: z.string(), sortOrder: z.number().int().default(0) });
export const budgetTemplateItem = z.object({ title: trimmed(120), category: z.string(), estimatedAmount: money.default(0), notes: trimmed(2000).default("") });
export const timelineTemplateItem = z.object({
  title: trimmed(120), offsetMinutes: z.number().int().min(0).max(2 * 1440), durationMinutes: z.number().int().min(0).max(1440).default(30),
  iconName: trimmed(40).default("sparkles"), colorHex: z.string().default("#E94B7C"), location: trimmed(240).default(""), notes: trimmed(2000).default(""),
});
/** Stored as JSON in `wedding_templates.payload_json`; parsed defensively on read. */
export const templatePayloadSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("checklist"), items: z.array(checklistTemplateItem).max(500) }),
  z.object({ kind: z.literal("budget"), items: z.array(budgetTemplateItem).max(500) }),
  z.object({ kind: z.literal("timeline"), items: z.array(timelineTemplateItem).max(500) }),
]);
export type TemplatePayload = z.infer<typeof templatePayloadSchema>;

export interface TemplateDTO {
  id: string;
  kind: TemplateKind;
  name: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

/* ───────────────────────── Account usage (all tiers) ───────────────────────── */

/** One owned wedding's live rows per capped section, plus its collaborators (caps bind to the owner). */
export interface WeddingUsageDTO {
  id: string;
  partnerOneName: string;
  partnerTwoName: string;
  archived: boolean;
  items: Record<ItemFeature, number>;
  /** Live non-owner memberships. */
  collaborators: number;
}

/** `GET /me/usage` — what the account holds against its ceilings; drives the Account page and upgrade prompts. */
export interface AccountUsageDTO {
  plan: Plan;
  packs: number;
  /**
   * `limit` is the tier's cap (+ packs). `unlimited`: no cap is enforced — the account holds an entitled App Store
   * purchase (`activeWeddingCeiling`); show "Unlimited", not `limit`.
   */
  weddings: { active: number; archived: number; limit: number; unlimited: boolean; countsArchived: boolean };
  storage: { usedBytes: number; limitBytes: number };
  templates: { count: number; limit: number };
  /** Per-section caps on this plan: `FREE_LIMITS` on Free, `null` (unlimited) on every paid tier. */
  itemLimits: Readonly<Record<PremiumFeature, number>> | null;
  /** Every wedding the account owns, active first — the ones the caps apply to. */
  ownedWeddings: WeddingUsageDTO[];
}

/** Error details carried by PLAN_LIMIT_REACHED. */
export interface PlanLimitDetails {
  feature: PlanFeature;
  limit: number;
  plan: Plan;
}

/* ───────────────────────── Planner dashboard (Pro) ───────────────────────── */

export interface PlannerWeddingDTO {
  id: string;
  partnerOneName: string;
  partnerTwoName: string;
  weddingDate: string;
  daysUntil: number;
  isPast: boolean;
  role: (typeof COLLABORATOR_ROLE)[number];
  isOwner: boolean;
  tasks: { done: number; total: number; overdue: number } | null;
  guests: { invited: number; attendingCount: number; pending: number } | null;
  /** Amounts are null when the viewer lacks financial access in that wedding. */
  paymentsDue30d: { count: number; amount: number | null; currencyCode: string };
  nextTask: { id: string; title: string; dueDate: string | null } | null;
}

export interface PlannerAgendaItemDTO {
  weddingId: string;
  coupleTitle: string;
  date: string;
  kind: "task" | "payment" | "wedding";
  title: string;
  amount: number | null;
  currencyCode: string | null;
}

/** `GET /me/planner` — every active wedding the caller belongs to, plus the next 30 days across all of them. */
export interface PlannerDTO {
  weddings: PlannerWeddingDTO[];
  agenda: PlannerAgendaItemDTO[];
  generatedAt: string;
}
